import { throwError, from } from 'rxjs';
import { map, flatMap } from 'rxjs/operators';
import { withLogging } from '@chr/common-http-interceptors-rxjs';
import { ajax } from 'rxjs/ajax';
import { v4 } from 'uuid';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var authInterceptor = function authInterceptor(client) {
  return function (request, next) {
    if (!isAuthClientAsync(client) && !client.isLoggedIn()) {
      return throwError('User is not currently authenticated - need to refresh token');
    }

    var accessTokenPromise = isAuthClientAsync(client) ? client.getAccessToken() : Promise.resolve(client.isLoggedIn() ? client.accessToken : '');
    var accessTokenObservable = from(accessTokenPromise);
    var mappedObservable = accessTokenObservable.pipe(map(function (token) {
      request.headers = request.headers || {};

      if (!request.headers['Authorization']) {
        request.headers['Authorization'] = "BEARER ".concat(token);
      }

      return request;
    }), flatMap(function (request) {
      return next(request);
    }));
    return mappedObservable;
  };
};

var isAuthClientAsync = function isAuthClientAsync(client) {
  return typeof client.getAccessToken === 'function';
};

var conventionsInterceptor = function conventionsInterceptor(request, next) {
  request.headers = request.headers || {};
  var keys = Object.keys(request.headers);
  var accept = keys.find(function (k) {
    return k.toLowerCase() === 'accept';
  });
  var contentType = keys.find(function (k) {
    return k.toLowerCase() === 'content-type';
  });

  if (!accept) {
    request.headers['Accept'] = 'application/json';
  }

  if (!contentType) {
    request.headers['Content-Type'] = 'application/json';
  }

  return next(request);
};

var loggingInterceptor = function loggingInterceptor(request, next) {
  return next(request).pipe(withLogging());
};

var isSameHost = function isSameHost(url) {
  url = url.toLowerCase();
  var currentHost = "".concat(location.protocol, "//").concat(location.host);
  return url.startsWith(currentHost);
};

var fixedRequest = function fixedRequest(request) {
  if (typeof request === 'string') {
    return {
      url: request,
      crossDomain: !isSameHost(request)
    };
  }

  return _objectSpread2({
    crossDomain: !isSameHost(request.url)
  }, request);
};

var createRequest = function createRequest(method, url, body, headers) {
  return {
    method: method,
    url: url,
    body: body,
    headers: headers,
    crossDomain: !isSameHost(url)
  };
};

var createAjax = function createAjax(method) {
  return function (url, headers) {
    return ajax(createRequest(method, url, null, headers));
  };
};

var createAjaxWithBody = function createAjaxWithBody(method) {
  return function (url, body, headers) {
    return ajax(createRequest(method, url, body, headers));
  };
};

var fixedAjax = Object.assign(function (request) {
  return ajax(fixedRequest(request));
}, {
  get: createAjax('GET'),
  put: createAjaxWithBody('PUT'),
  post: createAjaxWithBody('POST'),
  patch: createAjaxWithBody('PATCH'),
  "delete": createAjax('DELETE'),
  getJSON: function getJSON(url, headers) {
    return ajax({
      url: url,
      method: 'GET',
      responseType: 'json',
      crossDomain: !isSameHost(url),
      headers: headers
    }).pipe(map(function (response) {
      return response.response;
    }));
  }
});

var correlationInterceptor = function correlationInterceptor(request, next) {
  request.headers = request.headers || {};
  var keys = Object.keys(request.headers);
  var correlationId = keys.find(function (k) {
    return k.toLowerCase() === 'x-correlationid';
  });

  if (!correlationId) {
    request.headers['X-CorrelationId'] = v4();
  }

  return next(request);
};

var AjaxClient = /*#__PURE__*/function () {
  function AjaxClient(middlewares, ajaxCreator) {
    _classCallCheck(this, AjaxClient);

    this.middlewares = middlewares;
    this.ajaxCreator = ajaxCreator;
  }

  _createClass(AjaxClient, [{
    key: "get",
    value: function get(request) {
      var _this = this;

      return this.withMiddleware(request, function (r) {
        return _this.ajaxCreator.get(r.url, r.headers);
      });
    }
  }, {
    key: "post",
    value: function post(request) {
      var _this2 = this;

      return this.withMiddleware(request, function (r) {
        return _this2.ajaxCreator.post(r.url, r.body, r.headers);
      });
    }
  }, {
    key: "put",
    value: function put(request) {
      var _this3 = this;

      return this.withMiddleware(request, function (r) {
        return _this3.ajaxCreator.put(r.url, r.body, r.headers);
      });
    }
  }, {
    key: "patch",
    value: function patch(request) {
      var _this4 = this;

      return this.withMiddleware(request, function (r) {
        return _this4.ajaxCreator.patch(r.url, r.body, r.headers);
      });
    }
  }, {
    key: "delete",
    value: function _delete(request) {
      var _this5 = this;

      return this.withMiddleware(request, function (r) {
        return _this5.ajaxCreator["delete"](r.url, r.headers);
      });
    }
  }, {
    key: "withMiddleware",
    value: function withMiddleware(request, baseMethod) {
      var result = this.middlewares.reduceRight(function (agg, curr) {
        return function (request) {
          return curr(request, agg);
        };
      }, baseMethod);
      return result(request);
    }
  }], [{
    key: "createDefault",
    value: function createDefault(authClient) {
      return new AjaxClient([authInterceptor(authClient), conventionsInterceptor, loggingInterceptor, correlationInterceptor], fixedAjax);
    }
  }]);

  return AjaxClient;
}();

export { AjaxClient, authInterceptor, conventionsInterceptor, correlationInterceptor, fixedAjax, loggingInterceptor };
